import { useState, useEffect, useCallback } from 'react';
import { fetchInquiries, updateInquiryStatus, deleteInquiry, type Inquiry } from '@/lib/api';

const statusColors: Record<string, string> = {
  pending: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  review: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
};

const statusLabels: Record<string, string> = {
  pending: 'Pending',
  review: 'Review',
};

export default function AdminInquiries() {
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selected, setSelected] = useState<Inquiry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);

  const loadData = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    setError('');
    try {
      const data = await fetchInquiries();
      // Show all active (pending, review, or non-completed) inquiries in active list
      const activeOnly = data.filter((i) => (i.status || 'pending').toLowerCase() !== 'completed');
      setInquiries(activeOnly);
    } catch {
      setError('Unable to load inquiries. Please try again.');
      setInquiries([]);
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(true);

    const handleUpdate = () => {
      loadData(false);
    };

    window.addEventListener('inquiries_updated', handleUpdate);
    window.addEventListener('storage', handleUpdate);

    return () => {
      window.removeEventListener('inquiries_updated', handleUpdate);
      window.removeEventListener('storage', handleUpdate);
    };
  }, [loadData]);

  const filtered = inquiries.filter((inq) => {
    const s = search.toLowerCase();
    const matchesSearch =
      (inq.name || '').toLowerCase().includes(s) ||
      (inq.company || '').toLowerCase().includes(s) ||
      (inq.email || '').toLowerCase().includes(s) ||
      (inq.phone || '').toLowerCase().includes(s) ||
      (inq.message || '').toLowerCase().includes(s);
    const itemStatus = (inq.status || 'pending').toLowerCase();
    const matchesStatus =
      statusFilter === 'All' || itemStatus === statusFilter.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  const handleUpdateStatus = async (id: string, status: string) => {
    setWorking(true);
    setError('');
    try {
      const success = await updateInquiryStatus(id, status);
      if (!success) throw new Error('Status update failed');
      setSelected(null);
      loadData();
    } catch {
      setError('Failed to update inquiry. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  const handleDelete = async (id: string) => {
    setWorking(true);
    setError('');
    try {
      const success = await deleteInquiry(id);
      if (!success) throw new Error('Delete failed');
      setSelected(null);
      loadData();
    } catch {
      setError('Failed to delete inquiry. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Contact Inquiries</h1>
          <p className="text-sm text-white/50 mt-1">Manage and respond to website project inquiries.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-white/40">{filtered.length} inquiries</span>
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => loadData()} className="text-red-300 hover:text-red-200 font-medium">
            Retry
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, company, email, or message..."
            className="w-full pl-9 pr-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition"
          />
        </div>
        <div className="flex gap-2">
          {['All', 'pending', 'review'].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-4 py-2.5 rounded-full text-sm font-medium transition whitespace-nowrap ${statusFilter === s
                ? 'bg-neon-500/20 text-neon-300 border border-neon-500/40'
                : 'glass text-white/60 border border-white/5 hover:border-white/20 hover:text-white'
                }`}
            >
              {s === 'All' ? 'All' : statusLabels[s]}
            </button>
          ))}
        </div>
      </div>

      {/* Inquiries Table */}
      <div className="glass rounded-xl border border-white/5 overflow-hidden">
        {loading ? (
          <div className="py-20 text-center text-white/50 text-sm">
            <i className="ri-loader-4-line animate-spin mr-2" />
            Loading inquiries...
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-neon-500/10 text-neon-400 text-2xl mb-4">
              <i className="ri-mail-unread-line" />
            </div>
            <h3 className="font-display font-semibold text-white">No inquiries</h3>
            <p className="mt-2 text-sm text-white/50">New inquiries submitted through the Contact form will appear here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/5 text-white/50 text-xs uppercase tracking-wider">
                  <th className="px-4 py-3 text-left">Name & Company</th>
                  <th className="px-4 py-3 text-left">Contact Info</th>
                  <th className="px-4 py-3 text-left">Message</th>
                  <th className="px-4 py-3 text-left">Date / Time</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((inq) => (
                  <tr key={inq.id} className="border-b border-white/5 hover:bg-white/[0.02] transition">
                    <td className="px-4 py-4">
                      <div className="font-medium text-white">{inq.name}</div>
                      {inq.company && <div className="text-xs text-neon-300 font-mono">{inq.company}</div>}
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-white/80">{inq.email || '—'}</div>
                      {inq.phone && <div className="text-xs text-white/40">{inq.phone}</div>}
                    </td>
                    <td className="px-4 py-4 text-white/70 max-w-xs truncate" title={inq.message || ''}>
                      {inq.message || '—'}
                    </td>
                    <td className="px-4 py-4 text-white/50 font-mono text-xs whitespace-nowrap">
                      {inq.created_at ? new Date(inq.created_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-4">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium border ${statusColors[(inq.status || 'pending').toLowerCase()] || statusColors.pending}`}>
                        {statusLabels[(inq.status || 'pending').toLowerCase()] || inq.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setSelected(inq)}
                          className="text-xs text-neon-400 hover:text-neon-300 font-medium transition px-2 py-1 rounded bg-neon-500/10 hover:bg-neon-500/20"
                        >
                          View Details
                        </button>
                        <button
                          onClick={() => handleDelete(inq.id)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-red-400 hover:bg-red-500/10 transition"
                          title="Delete Inquiry"
                        >
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Inquiry Detail Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="glass rounded-2xl border border-white/10 max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display font-semibold text-lg text-white">Inquiry Details</h2>
              <button onClick={() => setSelected(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/5 text-white/50 hover:text-white transition">
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-neon-600 to-neon-400 flex items-center justify-center text-white font-bold text-sm">
                  {(selected.name || '?').charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="font-medium text-white text-base">{selected.name}</div>
                  {selected.company && <div className="text-xs font-mono text-neon-300">Company: {selected.company}</div>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-white/5">
                  <div className="text-xs text-white/40 mb-1">Email</div>
                  <div className="text-sm text-white/90 truncate" title={selected.email || ''}>{selected.email || '—'}</div>
                </div>
                <div className="p-3 rounded-lg bg-white/5">
                  <div className="text-xs text-white/40 mb-1">Contact Phone</div>
                  <div className="text-sm text-white/90">{selected.phone || '—'}</div>
                </div>
              </div>

              {(selected.service || selected.budget) && (
                <div className="grid grid-cols-2 gap-3">
                  {selected.service && (
                    <div className="p-3 rounded-lg bg-white/5">
                      <div className="text-xs text-white/40 mb-1">Service Requested</div>
                      <div className="text-sm text-white/90">{selected.service}</div>
                    </div>
                  )}
                  {selected.budget && (
                    <div className="p-3 rounded-lg bg-white/5">
                      <div className="text-xs text-white/40 mb-1">Estimated Budget</div>
                      <div className="text-sm text-white/90">{selected.budget}</div>
                    </div>
                  )}
                </div>
              )}

              <div className="p-3 rounded-lg bg-white/5">
                <div className="text-xs text-white/40 mb-1">Submitted Date & Time</div>
                <div className="text-sm text-white/90 font-mono">
                  {selected.created_at ? new Date(selected.created_at).toLocaleString() : '—'}
                </div>
              </div>

              <div className="p-3 rounded-lg bg-white/5">
                <div className="text-xs text-white/40 mb-1">Project Description / Message</div>
                <div className="text-sm text-white/90 leading-relaxed whitespace-pre-wrap">{selected.message || '—'}</div>
              </div>

              <div className="flex flex-col gap-2 pt-2">
                {(selected.status || 'pending').toLowerCase() === 'pending' && (
                  <button
                    onClick={() => handleUpdateStatus(selected.id, 'review')}
                    disabled={working}
                    className="btn-primary text-white font-semibold px-4 py-2.5 rounded-md text-sm whitespace-nowrap inline-flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <i className="ri-eye-line" /> Mark as Review
                  </button>
                )}
                <button
                  onClick={() => handleUpdateStatus(selected.id, 'completed')}
                  disabled={working}
                  className="px-4 py-2.5 rounded-md text-sm font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/25 transition whitespace-nowrap inline-flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <i className="ri-check-double-line" /> Mark as Complete
                </button>
                <button
                  onClick={() => handleDelete(selected.id)}
                  disabled={working}
                  className="px-4 py-2.5 rounded-md text-sm font-semibold bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition whitespace-nowrap inline-flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <i className="ri-delete-bin-line" /> Delete
                </button>
                <button
                  onClick={() => setSelected(null)}
                  className="px-4 py-2.5 rounded-md text-sm font-semibold border border-white/10 text-white/70 hover:bg-white/5 transition whitespace-nowrap"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}